import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOConfig {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'product' | 'article';
  noindex?: boolean;
  structuredData?: object;
  language?: 'en' | 'fa';
}

export function SEOHead({
  title,
  description,
  image,
  url,
  type = 'website',
  noindex = false,
  structuredData,
  language,
}: SEOConfig) {
  const { language: ctxLanguage, t } = useLanguage();
  const lang = language ?? ctxLanguage;
  const pageTitle = title ? t('seo.titleSuffix', { title }) : t('seo.defaultTitle');
  const pageDescription = description || t('seo.defaultDescription');
  const pageImage = image || '/og-image.png';
  const pageUrl = url || 'https://rubby.co.uk';

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="RUBBY" />
      <meta property="og:locale" content={lang === 'fa' ? 'fa_IR' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
